import {
  GridCallbackDetails,
  GridFilterItem,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction } from 'react';

import { StructureType } from '../../../types/types';
import { findStructure, isJson } from '../../../utils/dataTypes';

export const filterMap = (
  filters: GridFilterItem[] | undefined,
): GridFilterModel => ({
  items:
    typeof filters !== 'undefined' && Array.isArray(filters)
      ? filters.map((item: GridFilterItem, i: number) => ({
          ...item,
          id: i,
          operatorValue: item?.operator || 'equals',
        }))
      : [],
});

export const parseToInt = (param: string | string[]): number => {
  if (Array.isArray(param)) {
    return parseInt(param[0], 10) || 0;
  }
  return parseInt(param, 10) || 0;
};

export const decodeURI = (param: string | string[]): string | string[] => {
  if (Array.isArray(param)) {
    return param.map((item) => decodeURIComponent(item) || '');
  }
  return decodeURIComponent(param) || '';
};

export const lowercaseKeys = (
  obj: Record<string, unknown> | Array<Record<string, unknown>>,
): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseKeys);
  }
  if (obj && typeof obj === 'object') {
    const newObj: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      newObj[key.toLowerCase()] = lowercaseKeys(
        obj[key] as Record<string, unknown>,
      );
    }
    return newObj;
  }
  return obj;
};

interface RowWithOptionalId {
  id?: string | number;
  no?: string | number;
  [key: string]: unknown;
}

export const constructRows = (
  rows: ReadonlyArray<RowWithOptionalId> | undefined,
): Array<RowWithOptionalId & { rowIndex: number }> => {
  if (!Array.isArray(rows)) return [];

  const firstRow = rows[0] as RowWithOptionalId | undefined;
  const hasId =
    firstRow?.id &&
    (typeof firstRow.id === 'string' || typeof firstRow.id === 'number');
  const hasNo =
    firstRow?.no &&
    (typeof firstRow.no === 'string' || typeof firstRow.no === 'number');
  const hasUniqueId = hasId || hasNo;

  return rows.map((row: RowWithOptionalId, rowIndex: number) => ({
    ...row,
    id: hasUniqueId ? row.id || row.no : rowIndex,
    rowIndex,
  }));
};

const OPERATOR_MAPPINGS: Record<string, string> = {
  contains: 'contains',
  doesNotContain: 'doesnotcontain',
  equals: 'eq',
  doesNotEqual: 'neq',
  startsWith: 'startswith',
  endsWith: 'endswith',
  isEmpty: 'eq',
  isNotEmpty: 'neq',
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  is: 'eq',
  not: 'neq',
  after: 'gt',
  before: 'lt',
  onOrAfter: 'gte',
  onOrBefore: 'lte',
  isAnyOf: 'contains',
  modelEqual: 'eq',
  modelNotEqual: 'neq',
};

export const operatorMap = (operator: string): string =>
  OPERATOR_MAPPINGS[operator] || operator;

export interface FilterWithLogicType {
  field: string;
  operator: string;
  value: unknown;
  logic?: 'Or' | 'And';
}

const addLogicToFilters = ({
  filters,
  structure,
}: {
  filters: FilterWithLogicType[];
  structure: StructureType[];
}): FilterWithLogicType[] => {
  if (filters.length <= 1) return filters;

  let result: FilterWithLogicType[] = [];

  const grouped = filters.reduce<Record<string, FilterWithLogicType[]>>(
    (acc, condition) => {
      if (!acc[condition.field]) {
        acc[condition.field] = [];
      }
      acc[condition.field].push(condition);
      return acc;
    },
    {},
  );

  let groupIndex = 0;
  Object.entries(grouped).forEach(([key, groupFilters]) => {
    const filterStructure = findStructure(structure, key);

    if (groupFilters.length > 1) {
      result = [
        ...result,
        ...groupFilters.map((val, filterIndex) => ({
          ...val,
          ...(filterStructure?.Type === 'datetime' && {
            operator: filterIndex === 0 ? 'gte' : 'lte',
          }),
          logic:
            filterStructure?.Type === 'datetime'
              ? ('And' as const)
              : ('Or' as const),
        })),
      ];
    } else {
      if (result[groupIndex]) {
        result[groupIndex].logic = 'And';
      }
      result.push({ ...groupFilters[0], logic: 'And' });
    }

    groupIndex += 1;
  });

  delete result[result.length - 1].logic;
  return result;
};

const getColumnName = (
  field: string,
  sortStructure: StructureType,
): string => {
  if (!sortStructure) return field;

  if (isJson(sortStructure.ForeignKeyDefaultFilter)) {
    const filter = JSON.parse(sortStructure.ForeignKeyDefaultFilter) as {
      fields: string;
    };
    const fieldsArray = filter.fields.split(',');
    if (fieldsArray.length > 1) {
      return `${field}.${fieldsArray[1]}`;
    }
  }

  if (sortStructure.InnerStructure) {
    return `${field}.Name`;
  }

  return field;
};

export const prepareSort = (
  sortModel: GridSortModel,
  structure: StructureType[],
): Array<{ field: string; dir: string }> | null => {
  if (!sortModel.length) return null;

  return sortModel.map((sortItem) => {
    const sortStructure = findStructure(structure, sortItem.field);
    const colName = getColumnName(sortItem.field, sortStructure);
    return {
      field: colName,
      dir: sortItem.sort || 'asc',
    };
  });
};

export const handleFilterModelChange = (
  model: GridFilterModel,
  details: GridCallbackDetails<'filter'>,
  setFilter: Dispatch<SetStateAction<GridFilterModel | undefined>>,
) => {
  if (typeof details.reason === 'undefined') return;

  if (model.items.length === 0) {
    setFilter(model);
    return;
  }

  const newFiltersItems = model.items.map((item) => {
    const isArray = Array.isArray(item.value);
    let newValue = null;

    if (isArray) {
      const hasIds = item.value.some(
        (i: Record<string, unknown>) => i.Id,
      );
      newValue = hasIds
        ? item.value.map((i: Record<string, unknown>) => i.Id)
        : item.value;
    } else if (item?.value instanceof Date) {
      newValue = dayjs(item.value).format('YYYY-MM-DDTHH:mm:ss.SSS');
    } else {
      newValue = item.value;
    }

    return {
      id: item.id || item.field,
      field: item.field,
      operator: isArray ? 'isAnyOf' : item.operator,
      value: newValue,
    };
  });

  setFilter({ items: newFiltersItems || [] });
};

export const prepareFilters = (
  filters: GridFilterItem[],
  structure: StructureType[],
) => {
  if (!filters.length || !structure.length) return null;

  const filtersMap: FilterWithLogicType[] = [];

  filters.forEach((filter: GridFilterItem) => {
    const currentStructure = findStructure(structure, filter.field);

    if (['model', 'array', 'enum'].includes(currentStructure?.Type)) {
      filtersMap.push({
        field:
          currentStructure?.Type === 'enum'
            ? filter.field
            : `${filter.field}.Id`,
        operator: 'eq',
        value: filter?.value?.Id ? filter.value.Id : filter.value,
      });
    } else {
      filtersMap.push({
        field: filter?.value?.Id ? `${filter.field}.Id` : filter.field,
        operator:
          currentStructure?.Type === 'string' ? 'contains' : 'eq',
        value: filter?.value?.Id || filter?.value,
      });
    }
  });

  if (filtersMap.length > 0) {
    return {
      filters: addLogicToFilters({ filters: filtersMap, structure }),
    };
  }
  return null;
};
